/**
 * ════════════════════════════════════════════════════════════════
 * AdminOffers — Gestion des Offres Marketing (Admin)
 * ════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURE: Ce composant gère les Offers (couche marketing).
 * Les Offers ne calculent JAMAIS un prix — c'est le rôle d'ORU.
 * Les champs discount_percent, franco_threshold sont INFORMATIFS.
 *
 * Voir: OFFERS_ARCHITECTURE.md §2
 * ════════════════════════════════════════════════════════════════
 */
import React, { useState, useEffect, useCallback } from 'react';
import DashboardWrapper from '../../components/DashboardWrapper';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import GuardedDialog from '../../components/GuardedDialog';
import useFormDirtyState from '../../hooks/useFormDirtyState';
import { 
  Plus, Search, Filter, Edit, Trash2, Eye, EyeOff, 
  Tag, Percent, Gift, Package, Calendar, Building2,
  Globe, Lock, ChevronDown, ChevronUp, X, ShoppingCart, AlertCircle
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';
import ImageUploadField from '../../components/ImageUploadField';

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  // filterScope supprimé - visibilité basée sur product_ids
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const { hasUnsavedChanges, setDirty, resetDirty } = useFormDirtyState();
  const [formData, setFormData] = useState({
    offer_code: '',
    partner_id: '',
    title: '',
    description: '',
    type: 'remise',
    discount_percent: '',
    franco_threshold: '',
    goodies_description: '',
    gratuite_description: '',  // Nouveau
    minimum_order: '',
    maximum_order: '',
    multiple_order: '',
    min_order_unit: '€',  // Nouveau
    max_order_unit: '€',  // Nouveau
    start_date: '',
    end_date: '',
    is_active: true,
    status: 'active',
    image_url: '',
    badge_text: '',
    badge_color: '#ef4444',
    offer_url: '',  // Nouveau
    // V2: Scope type (PRODUCTS ou PARTNER)
    scope_type: 'PARTNER',  // Par défaut: tout le catalogue partenaire
    product_ids: [],
    apply_to_all_products: true  // Legacy - sera true si scope_type=PARTNER
  });
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Constantes
  const OFFER_TYPES = [
    { value: 'remise', label: 'Remise (%)' },
    { value: 'franco', label: 'Franco (€)' },
    { value: 'goodies', label: 'Goodies' },
    { value: 'gratuite', label: 'Gratuité' },
    { value: 'mixte', label: 'Mixte' },
  ];

  const ORDER_UNITS = [
    { value: '€', label: '€' },
    { value: 'unité', label: 'unité' }
  ];

  const loadOffers = useCallback(async () => {
    try {
      const response = await api.get('/offers?include_expired=true');
      setOffers(response.data || []);
    } catch (error) {
      toast.error('Erreur chargement des offres');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const response = await api.get('/partners?is_active=true');
      // Tri alphabétique A→Z pour les dropdowns
      const sortedPartners = (response.data || [])
        .slice()
        .sort((a, b) => {
          const nameA = (a.nom || a.name || '').toLowerCase().trim();
          const nameB = (b.nom || b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  }, []);

  const loadProducts = useCallback(async (partnerId = null) => {
    try {
      let url = '/products?limit=200';
      if (partnerId) {
        url += `&partner_id=${partnerId}`;
      }
      const response = await api.get(url);
      // API returns paginated format: { products: [...], total, offset, limit }
      const data = response.data;
      setProducts(Array.isArray(data) ? data : (data?.products || []));
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }, []);

  useEffect(() => {
    loadOffers();
    loadPartners();
    loadProducts();
  }, [loadOffers, loadPartners, loadProducts]);

  // Charger les produits du partenaire sélectionné
  useEffect(() => {
    if (formData.partner_id) {
      loadProducts(formData.partner_id);
    }
  }, [formData.partner_id, loadProducts]);

  // Filtrage des offres
  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPartner = filterPartner === 'all' || offer.partner_id === filterPartner;
    const matchesStatus = filterStatus === 'all' || offer.status === filterStatus;
    // Filtre visibility_scope supprimé - la visibilité est basée sur product_ids/partner_id
    
    return matchesSearch && matchesPartner && matchesStatus;
  });

  const getPartnerName = (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    return partner?.nom || partnerId;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'remise': return <Percent className="h-4 w-4" />;
      case 'franco': return <Package className="h-4 w-4" />;
      case 'goodies': return <Gift className="h-4 w-4" />;
      case 'mixte': return <Tag className="h-4 w-4" />;
      default: return <Tag className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800'
    };
    const labels = {
      draft: 'Brouillon',
      active: 'Active',
      archived: 'Archivée'
    };
    return <Badge className={colors[status] || colors.draft}>{labels[status] || status}</Badge>;
  };

  const openCreateModal = () => {
    setEditingOffer(null);
    setFormData({
      offer_code: '',
      partner_id: partners[0]?.id || '',
      title: '',
      description: '',
      type: 'remise',
      discount_percent: '',
      franco_threshold: '',
      goodies_description: '',
      gratuite_description: '',
      minimum_order: '',
      maximum_order: '',
      multiple_order: '',
      min_order_unit: '€',
      max_order_unit: '€',
      start_date: '',
      end_date: '',
      is_active: true,
      status: 'active',
      visibility_scope: 'partner',
      image_url: '',
      badge_text: '',
      badge_color: '#ef4444',
      offer_url: '',
      product_ids: [],
      apply_to_all_products: true
    });
    setProductSearch('');
    setModalOpen(true);
  };

  const openEditModal = (offer) => {
    setEditingOffer(offer);
    setFormData({
      offer_code: offer.offer_code || '',
      partner_id: offer.partner_id || '',
      title: offer.title || '',
      description: offer.description || '',
      type: offer.type || 'remise',
      discount_percent: offer.discount_percent || '',
      franco_threshold: offer.franco_threshold || '',
      goodies_description: offer.goodies_description || '',
      gratuite_description: offer.gratuite_description || '',
      minimum_order: offer.minimum_order || '',
      maximum_order: offer.maximum_order || '',
      multiple_order: offer.multiple_order || '',
      min_order_unit: offer.min_order_unit || '€',
      max_order_unit: offer.max_order_unit || '€',
      start_date: offer.start_date || '',
      end_date: offer.end_date || '',
      is_active: offer.is_active !== false,
      status: offer.status || 'active',
      image_url: offer.image_url || '',
      badge_text: offer.badge_text || '',
      badge_color: offer.badge_color || '#ef4444',
      offer_url: offer.offer_url || '',
      // V2: scope_type
      scope_type: offer.scope_type || (offer.product_ids?.length > 0 ? 'PRODUCTS' : 'PARTNER'),
      product_ids: offer.product_ids || [],
      apply_to_all_products: offer.apply_to_all_products !== false
    });
    setProductSearch('');
    setModalOpen(true);
    resetDirty();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.partner_id) {
      toast.error('Veuillez sélectionner un partenaire');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    if (!editingOffer && !formData.offer_code.trim()) {
      toast.error('Le code offre est obligatoire');
      return;
    }
    
    // V2: Validation conditionnelle basée sur scope_type
    if (formData.scope_type === 'PRODUCTS') {
      if (!formData.product_ids || formData.product_ids.length === 0) {
        toast.error('Veuillez sélectionner au moins un produit pour une offre PRODUCTS');
        return;
      }
    } else if (formData.scope_type === 'PARTNER') {
      if (!formData.partner_id) {
        toast.error('Veuillez sélectionner un partenaire pour une offre PARTNER');
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : null,
        franco_threshold: formData.franco_threshold ? parseFloat(formData.franco_threshold) : null,
        minimum_order: formData.minimum_order ? parseInt(formData.minimum_order) : null,
        maximum_order: formData.maximum_order ? parseInt(formData.maximum_order) : null,
        multiple_order: formData.multiple_order ? parseInt(formData.multiple_order) : null,
        // V2: Aligner product_ids et apply_to_all avec scope_type
        scope_type: formData.scope_type,
        product_ids: formData.scope_type === 'PRODUCTS' ? formData.product_ids : [],
        apply_to_all_products: formData.scope_type === 'PARTNER',
        // Pour PRODUCTS sans partner_id, le backend le déduira
        partner_id: formData.scope_type === 'PARTNER' ? formData.partner_id : (formData.partner_id || null)
      };
      
      if (editingOffer) {
        // Phase 2: offer_code immutable — ne pas envoyer en update
        const { offer_code: _, ...updatePayload } = payload;
        await api.put(`/offers/${editingOffer.id}`, updatePayload);
        toast.success('Offre mise à jour');
      } else {
        await api.post('/offers', payload);
        toast.success('Offre créée');
      }
      
      setModalOpen(false);
      resetDirty();
      loadOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/offers/${deleteTarget.id}`);
      toast.success('Offre supprimée');
      setDeleteTarget(null);
      loadOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleActive = async (offer) => {
    try {
      await api.put(`/offers/${offer.id}`, { is_active: !offer.is_active });
      loadOffers();
      toast.success(offer.is_active ? 'Offre désactivée' : 'Offre activée');
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  return (
    <DashboardWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Offres</h1>
            <p className="text-muted-foreground">
              Source unique de vérité pour toutes les promotions
            </p>
          </div>
          <Button onClick={openCreateModal} data-testid="create-offer-button">
            <Plus className="h-4 w-4 mr-2" />
            Créer une offre
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{offers.length}</div>
              <div className="text-sm text-muted-foreground">Total Offres</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">
                {offers.filter(o => o.status === 'active' && o.is_active).length}
              </div>
              <div className="text-sm text-muted-foreground">Actives</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">
                {offers.filter(o => o.apply_to_all_products).length}
              </div>
              <div className="text-sm text-muted-foreground">Tous produits</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">
                {offers.filter(o => o.status === 'draft').length}
              </div>
              <div className="text-sm text-muted-foreground">Brouillons</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher une offre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-offers-input"
                  />
                </div>
              </div>
              
              <Select value={filterPartner} onValueChange={setFilterPartner}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-partner-select">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Partenaire" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les partenaires</SelectItem>
                  {partners.map(partner => (
                    <SelectItem key={partner.id} value={partner.id}>{partner.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-status-select">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="archived">Archivée</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Filtre visibilité supprimé - scope basé sur product_ids/partner_id */}
            </div>
          </CardContent>
        </Card>

        {/* Offers List */}
        <div className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
                <p className="mt-4 text-muted-foreground">Chargement...</p>
              </CardContent>
            </Card>
          ) : filteredOffers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Tag className="h-12 w-12 mx-auto text-gray-300" />
                <p className="mt-4 text-muted-foreground">Aucune offre trouvée</p>
                <Button className="mt-4" onClick={openCreateModal}>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer la première offre
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredOffers.map(offer => (
                <Card key={offer.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Image - Content Fit: contain pour éviter crop */}
                      {offer.image_url && (
                        <div className="w-full md:w-24 h-24 rounded-lg overflow-hidden bg-slate-50 flex-shrink-0 flex items-center justify-center">
                          <img 
                            src={offer.image_url} 
                            alt={offer.title}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {getTypeIcon(offer.type)}
                          <h3 className="font-semibold truncate max-w-[200px]" title={offer.title}>{offer.title}</h3>
                          {offer.offer_code && (
                            <Badge variant="secondary" className="font-mono text-[10px]" data-testid={`offer-code-badge-${offer.id}`}>
                              {offer.offer_code}
                            </Badge>
                          )}
                          {getStatusBadge(offer.status)}
                          {offer.apply_to_all_products ? (
                            <Badge variant="outline" className="gap-1">
                              <Package className="h-3 w-3" /> Tous produits
                            </Badge>
                          ) : offer.product_ids?.length > 0 ? (
                            <Badge variant="outline" className="gap-1">
                              <ShoppingCart className="h-3 w-3" /> {offer.product_ids.length} produit(s)
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-orange-600 border-orange-300">
                              <AlertCircle className="h-3 w-3" /> Aucun produit
                            </Badge>
                          )}
                          {offer.badge_text && (
                            <Badge 
                              style={{ backgroundColor: offer.badge_color || '#ef4444' }}
                              className="text-white"
                            >
                              {offer.badge_text}
                            </Badge>
                          )}
                          {/* Phase 3: Conditions d'achat linking stats */}
                          {offer.linked_mechanics_count > 0 && (
                            <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-300" data-testid={`linked-oru-badge-${offer.id}`}>
                              <Tag className="h-3 w-3" /> {offer.linked_mechanics_count} condition(s) · {offer.linked_products_count} produit(s)
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground truncate max-w-[400px]" title={offer.description}>{offer.description}</p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {getPartnerName(offer.partner_id)}
                          </span>
                          {offer.discount_percent && (
                            <span className="flex items-center gap-1">
                              <Percent className="h-3 w-3" />
                              -{offer.discount_percent}%
                            </span>
                          )}
                          {offer.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {offer.start_date} → {offer.end_date || '∞'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(offer)}
                          title={offer.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {offer.is_active ? (
                            <Eye className="h-4 w-4 text-green-600" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(offer)}
                          data-testid={`edit-offer-${offer.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(offer)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-offer-${offer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <GuardedDialog open={modalOpen} onOpenChange={setModalOpen} isDirty={hasUnsavedChanges} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOffer ? 'Modifier l\'offre' : 'Créer une offre'}
            </DialogTitle>
            <DialogDescription>
              {editingOffer ? 'Modifiez les détails de l\'offre' : 'Créez une nouvelle offre promotionnelle'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} onChange={() => setDirty()} className="space-y-6">
            {/* Section Identité */}
            <div className="space-y-4">
              <h4 className="font-semibold border-b pb-2">Identité de l'offre</h4>
              
              {/* Phase 2: offer_code — identifiant technique */}
              <div className="space-y-2">
                <Label>
                  Code Offre <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.offer_code}
                  onChange={(e) => { setFormData({...formData, offer_code: e.target.value.toUpperCase().replace(/\s+/g, '-')}); setDirty(); }}
                  placeholder="ex: PROMO-ETE-2026-BLACKROCK"
                  disabled={!!editingOffer}
                  data-testid="offer-code-input"
                />
                <p className="text-xs text-muted-foreground">
                  Identifiant technique unique. {editingOffer ? 'Non modifiable après création.' : 'Sera utilisé pour les imports et synchronisations.'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Partenaire: requis seulement pour scope_type=PARTNER */}
                <div className="space-y-2">
                  <Label>
                    Partenaire {formData.scope_type === 'PARTNER' && <span className="text-red-500">*</span>}
                  </Label>
                  <Select
                    value={formData.partner_id}
                    onValueChange={(v) => setFormData({...formData, partner_id: v})}
                    disabled={formData.scope_type === 'PRODUCTS'}
                  >
                    <SelectTrigger data-testid="offer-partner-select">
                      <SelectValue placeholder={
                        formData.scope_type === 'PRODUCTS' 
                          ? "Déduit des produits sélectionnés" 
                          : "Sélectionner un partenaire"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {partners.map(partner => (
                        <SelectItem key={partner.id} value={partner.id}>{partner.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.scope_type === 'PRODUCTS' && (
                    <p className="text-xs text-muted-foreground">
                      Le partenaire sera déduit automatiquement des produits sélectionnés
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Type d'offre</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(newType) => {
                      // Reset des champs non pertinents selon le nouveau type
                      const updates = { type: newType };
                      if (newType !== 'remise' && newType !== 'mixte') {
                        updates.discount_percent = '';
                      }
                      if (newType !== 'franco' && newType !== 'mixte') {
                        updates.franco_threshold = '';
                      }
                      if (newType !== 'goodies' && newType !== 'mixte') {
                        updates.goodies_description = '';
                      }
                      if (newType !== 'gratuite' && newType !== 'mixte') {
                        updates.gratuite_description = '';
                      }
                      setFormData({...formData, ...updates});
                    }}
                  >
                    <SelectTrigger data-testid="offer-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OFFER_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ex: -20% sur toute la gamme"
                  data-testid="offer-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Détails de l'offre..."
                  rows={3}
                  data-testid="offer-description-input"
                />
              </div>
            </div>

            {/* Section Paramètres */}
            <div className="space-y-4">
              <h4 className="font-semibold border-b pb-2">Paramètres de l'offre</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(formData.type === 'remise' || formData.type === 'mixte') && (
                  <div className="space-y-2">
                    <Label>Remise (%)</Label>
                    <Input
                      type="number"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                      placeholder="Ex: 20"
                      min="0"
                      max="100"
                      data-testid="offer-discount-input"
                    />
                  </div>
                )}
                
                {(formData.type === 'franco' || formData.type === 'mixte') && (
                  <div className="space-y-2">
                    <Label>Franco (€)</Label>
                    <Input
                      type="number"
                      value={formData.franco_threshold}
                      onChange={(e) => setFormData({...formData, franco_threshold: e.target.value})}
                      placeholder="Ex: 200"
                      min="0"
                      data-testid="offer-franco-input"
                    />
                  </div>
                )}
                
                {(formData.type === 'goodies' || formData.type === 'mixte') && (
                  <div className="space-y-2 md:col-span-2">
                    <Label>Description goodies</Label>
                    <Input
                      value={formData.goodies_description}
                      onChange={(e) => setFormData({...formData, goodies_description: e.target.value})}
                      placeholder="Ex: 1 poster offert"
                      data-testid="offer-goodies-input"
                    />
                  </div>
                )}
              </div>
              
              {/* Champ Gratuité - visible UNIQUEMENT si type gratuite ou mixte */}
              {(formData.type === 'gratuite' || formData.type === 'mixte') && (
                <div className={`space-y-2 ${formData.type === 'gratuite' ? 'bg-pink-50 p-3 rounded-lg border border-pink-200' : ''}`}>
                  <Label className={formData.type === 'gratuite' ? 'font-semibold text-pink-800' : ''}>
                    Gratuité {formData.type === 'gratuite' && '*'}
                  </Label>
                  <Input
                    value={formData.gratuite_description}
                    onChange={(e) => setFormData({...formData, gratuite_description: e.target.value})}
                    placeholder="Ex: 1 produit offert, Livraison offerte..."
                    maxLength={200}
                    data-testid="gratuite-description-input"
                  />
                  <p className="text-xs text-muted-foreground">Texte libre décrivant la gratuité</p>
                </div>
              )}
              
              {/* Commandes min/max avec unités */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Commande min.</Label>
                  <Input
                    type="number"
                    value={formData.minimum_order}
                    onChange={(e) => setFormData({...formData, minimum_order: e.target.value})}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unité min.</Label>
                  <Select
                    value={formData.min_order_unit}
                    onValueChange={(v) => setFormData({...formData, min_order_unit: v})}
                  >
                    <SelectTrigger data-testid="min-order-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_UNITS.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Commande max.</Label>
                  <Input
                    type="number"
                    value={formData.maximum_order}
                    onChange={(e) => setFormData({...formData, maximum_order: e.target.value})}
                    placeholder="Illimité"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unité max.</Label>
                  <Select
                    value={formData.max_order_unit}
                    onValueChange={(v) => setFormData({...formData, max_order_unit: v})}
                  >
                    <SelectTrigger data-testid="max-order-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORDER_UNITS.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Multiple de commande + Lien URL */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Multiple commande</Label>
                  <Input
                    type="number"
                    value={formData.multiple_order}
                    onChange={(e) => setFormData({...formData, multiple_order: e.target.value})}
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lien vers l'offre</Label>
                  <Input
                    type="url"
                    value={formData.offer_url}
                    onChange={(e) => setFormData({...formData, offer_url: e.target.value})}
                    placeholder="https://..."
                    data-testid="offer-url-input"
                  />
                </div>
              </div>
            </div>

            {/* Section Dates & Statut */}
            <div className="space-y-4">
              <h4 className="font-semibold border-b pb-2">Période & Statut</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="offer-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    data-testid="offer-end-date"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger data-testid="offer-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archivée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Champ visibility_scope supprimé - la visibilité est maintenant basée sur product_ids/partner_id */}
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                  data-testid="offer-active-switch"
                />
                <Label>Offre active</Label>
              </div>
            </div>

            {/* Section Affichage CMS */}
            <div className="space-y-4">
              <h4 className="font-semibold border-b pb-2">Affichage CMS (optionnel)</h4>
              
              <ImageUploadField
                value={formData.image_url}
                onChange={(url) => setFormData({...formData, image_url: url})}
                label="Image de l'offre"
                data-testid="offer-image-input"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Badge texte</Label>
                  <Input
                    value={formData.badge_text}
                    onChange={(e) => setFormData({...formData, badge_text: e.target.value})}
                    placeholder="Ex: -20%"
                    data-testid="offer-badge-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Badge couleur</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.badge_color}
                      onChange={(e) => setFormData({...formData, badge_color: e.target.value})}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={formData.badge_color}
                      onChange={(e) => setFormData({...formData, badge_color: e.target.value})}
                      placeholder="#ef4444"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section Produits Concernés - V2 Scope Type */}
            <div className="space-y-4">
              <h4 className="font-semibold border-b pb-2 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Portée de l'offre
              </h4>
              
              {/* V2: Sélecteur de scope_type */}
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.scope_type === 'PARTNER' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData({
                    ...formData, 
                    scope_type: 'PARTNER',
                    apply_to_all_products: true,
                    product_ids: []
                  })}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Tout le catalogue</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    L'offre s'applique à tous les produits du partenaire sélectionné
                  </p>
                </div>
                
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.scope_type === 'PRODUCTS' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData({
                    ...formData, 
                    scope_type: 'PRODUCTS',
                    apply_to_all_products: false
                  })}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Produits sélectionnés</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    L'offre s'applique uniquement aux produits choisis ci-dessous
                  </p>
                </div>
              </div>
              
              {/* Sélection de produits spécifiques (si scope_type = PRODUCTS) */}
              {formData.scope_type === 'PRODUCTS' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher un produit..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      className="pl-10"
                      data-testid="product-search-input"
                    />
                  </div>
                  
                  {/* Produits sélectionnés */}
                  {formData.product_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.product_ids.map(productId => {
                        const product = products.find(p => p.id === productId);
                        return (
                          <Badge 
                            key={productId} 
                            variant="secondary"
                            className="flex items-center gap-1 pr-1"
                          >
                            {product?.intitule || product?.name || productId}
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                product_ids: formData.product_ids.filter(id => id !== productId)
                              })}
                              className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Liste des produits disponibles */}
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {products
                      .filter(p => {
                        const productName = (p.intitule || p.name || '').toLowerCase();
                        return productName.includes(productSearch.toLowerCase()) &&
                          !formData.product_ids.includes(p.id);
                      })
                      .slice(0, 20)
                      .map(product => (
                        <div
                          key={product.id}
                          className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => setFormData({
                            ...formData,
                            product_ids: [...formData.product_ids, product.id]
                          })}
                        >
                          {product.visuels?.[0] ? (
                            <img 
                              src={product.visuels[0]} 
                              alt="" 
                              className="w-8 h-8 object-cover rounded"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                              <Package className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{product.intitule || product.name}</p>
                            {product.prix_vente_conseille && (
                              <p className="text-xs text-muted-foreground">{product.prix_vente_conseille}€</p>
                            )}
                          </div>
                          <Plus className="h-4 w-4 text-gray-400" />
                        </div>
                      ))}
                    
                    {products.filter(p => {
                      const productName = (p.intitule || p.name || '').toLowerCase();
                      return productName.includes(productSearch.toLowerCase()) &&
                        !formData.product_ids.includes(p.id);
                    }).length === 0 && (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        {formData.partner_id 
                          ? 'Aucun produit trouvé pour ce partenaire'
                          : 'Sélectionnez d\'abord un partenaire'
                        }
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    {formData.product_ids.length} produit(s) sélectionné(s)
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                variant="secondary"
                onClick={() => {
                  if (editingOffer) {
                    window.open(`/dashboard/offer-preview/${editingOffer.id}`, '_blank');
                  } else {
                    const previewData = encodeURIComponent(JSON.stringify(formData));
                    window.open(`/dashboard/offer-preview/new?data=${previewData}`, '_blank');
                  }
                }}
                data-testid="preview-offer-button"
              >
                <Eye className="h-4 w-4 mr-2" />
                Prévisualiser
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-offer-button">
                {saving ? 'Enregistrement...' : editingOffer ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
      </GuardedDialog>

      <DestructiveActionDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Supprimer l'offre"
        itemName={deleteTarget?.title || ''}
        description="L'offre et ses conditions associées seront définitivement supprimées."
        onConfirm={handleDelete}
      />
    </DashboardWrapper>
  );
};

export default AdminOffers;
